import React, { useEffect, useState } from "react";
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import { Global } from '@emotion/react';

// basic: for metaobject rich text fields (not HTML like product descriptions).
// Hides strikethrough, alignment, images and horizontal rule.
const RichTextEditor = ({ value, onChange, basic = false }) => {
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image,
      Link.configure({ openOnClick: false }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const text = editor.getText().trim();
      onChange?.(text.length > 0 ? html : '');
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  if (!editor) return null;

  const insertImage = () => setOpenImageDialog(true);
  const handleAddImage = () => {
    if (imageUrl.trim()) {
      editor.chain().focus().setImage({ src: imageUrl.trim() }).run();
    }
    setImageUrl("");
    setOpenImageDialog(false);
  };
  const handleCancelImage = () => {
    setImageUrl("");
    setOpenImageDialog(false);
  };

  const insertLink = () => {
    const current = editor.getAttributes("link").href || "";
    setLinkUrl(current);
    setOpenLinkDialog(true);
  };
  const handleAddLink = () => {
    if (!linkUrl.trim()) {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().setLink({ href: linkUrl.trim() }).run();
    }
    setLinkUrl("");
    setOpenLinkDialog(false);
  };
  const handleCancelLink = () => {
    setLinkUrl("");
    setOpenLinkDialog(false);
  };

  const buttonStyle = {
    minWidth: 32,
    color: 'text.primary',
    '&:hover': {
      backgroundColor: 'transparent',
      color: 'primary.main',
    },
  };

  return (
    <>
      <Global styles={{
        '.tiptap.ProseMirror': {
          minHeight: '150px',
          cursor: 'text',
        }
      }} />
      <Box>
        <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
          <Select
            size="small"
            variant="outlined"
            defaultValue="paragraph"
            onChange={(event) => {
              const level = event.target.value;
              if (level === 'paragraph') {
                editor.chain().focus().setParagraph().run();
              } else {
                editor.chain().focus().toggleHeading({ level }).run();
              }
            }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="paragraph">Paragraph</MenuItem>
            <MenuItem value={1}>Heading 1</MenuItem>
            <MenuItem value={2}>Heading 2</MenuItem>
            <MenuItem value={3}>Heading 3</MenuItem>
          </Select>
          <Button
            size="small"
            onClick={() => editor.chain().focus().toggleBold().run()}
            color={editor.isActive('bold') ? 'primary' : 'default'}
            sx={buttonStyle}
          >
            <FormatBoldIcon fontSize="small" />
          </Button>
          <Button
            size="small"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            color={editor.isActive('italic') ? 'primary' : 'default'}
            sx={buttonStyle}
          >
            <FormatItalicIcon fontSize="small" />
          </Button>
          {!basic && (
            <Button
              size="small"
              onClick={() => editor.chain().focus().toggleStrike().run()}
              color={editor.isActive('strike') ? 'primary' : 'default'}
              sx={buttonStyle}
            >
              <FormatStrikethroughIcon fontSize="small" />
            </Button>
          )}
          <Button
            size="small"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            color={editor.isActive('bulletList') ? 'primary' : 'default'}
            sx={buttonStyle}
          >
            <FormatListBulletedIcon fontSize="small" />
          </Button>
          <Button
            size="small"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            color={editor.isActive('orderedList') ? 'primary' : 'default'}
            sx={buttonStyle}
          >
            <FormatListNumberedIcon fontSize="small" />
          </Button>
          {!basic && (
            <>
              <Button
                size="small"
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
                sx={buttonStyle}
              >
                <FormatAlignLeftIcon fontSize="small" />
              </Button>
              <Button
                size="small"
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
                sx={buttonStyle}
              >
                <FormatAlignCenterIcon fontSize="small" />
              </Button>
              <Button
                size="small"
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
                sx={buttonStyle}
              >
                <FormatAlignRightIcon fontSize="small" />
              </Button>
              <Button
                size="small"
                onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                color={editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'default'}
                sx={buttonStyle}
              >
                <FormatAlignJustifyIcon fontSize="small" />
              </Button>
            </>
          )}

          {!basic && (
            <Button onClick={insertImage} sx={buttonStyle}>
              <ImageIcon fontSize="small" />
            </Button>
          )}
          <Button
            onClick={insertLink}
            color={editor.isActive("link") ? "primary" : "default"}
            sx={buttonStyle}
          >
            <LinkIcon fontSize="small" />
          </Button>
          {!basic && (
            <Button
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
              sx={buttonStyle}
            >
              <HorizontalRuleIcon fontSize="small" />
            </Button>
          )}
        </Box>

        <Paper
          variant="outlined"
          sx={{
            padding: '4px 18px',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: 4,
            minHeight: 150,
            outline: 'none',
          }}
        >
          <EditorContent editor={editor} />
        </Paper>
      </Box>

      <Dialog open={openImageDialog} onClose={handleCancelImage} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Image</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Image URL"
            type="url"
            fullWidth
            variant="outlined"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelImage}>Cancel</Button>
          <Button onClick={handleAddImage} variant="contained">
            Insert
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openLinkDialog} onClose={handleCancelLink} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Link URL"
            type="url"
            fullWidth
            variant="outlined"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLink}>Cancel</Button>
          <Button onClick={handleAddLink} variant="contained">
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RichTextEditor;